import { get, post, put } from "aws-amplify/api";
import { default as React, useState, useEffect, useCallback, useMemo } from "react";
import { GenericTable } from "components/GenericTable";
import EditableCell from "containers/common/EditableCell";
import { Alert, Button, Col, Row } from "react-bootstrap";
import "react-datepicker/dist/react-datepicker.css";
import { Link } from "react-router-dom";
import { PacmanLoader } from "react-spinners";
import "containers/common/List.css";
import "containers/common/Table.css";
import { createElementIdFactory, createReusableTableElementIds } from "utils/elementIds";
import { PAGE_SIZES } from "utils/pageSizeConfig";

import {
  createColumnHelper,
} from "@tanstack/react-table";

const columnHelper = createColumnHelper();

function CityscopeList({ isAuthenticated, user }) {
  const cityscopeIdPrefix = "cityscope-list";

  const [isLoading, setIsLoading] = useState(true);
  const [ui, setUi] = useState({ showAlert: false, errorMessage: "" });
  const [sorting, setSorting] = useState([]);
  const [data, setData] = useState([]);

  // --- API Functions ---
  const getData = useCallback(async () => {
    const response = await get({
      apiName: "colin",
      path: "/cityscopes",
    }).response;
    try {
      const cs = await response.body.json();
      return cs;
    } catch (error) {
      alert(error + ". Please refresh the page (F5) to try again.");
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    (async () => {
      try {
        const localCityscopes = await getData();
        setData(localCityscopes || []);
        setIsLoading(false);
      } catch (e) {
        alert(e);
      }
      document.title = "Cityscope lookup";
    })();
  }, [isAuthenticated, getData]);

  const saveRecord = async (record) => {
    const isNew = record.new_record;
    const path = isNew ? "/cityscopes/new" : `/cityscopes/${record["cityscope"]}`;
    const method = isNew ? post : put;

    try {
      const result = await method({
        apiName: "colin",
        path: path,
        options: { body: { ...record, last_edited_by: user } }
      });
      const response = await result.response;
      return await response.body.json();
    } catch (err) {
      console.error("Save error:", err);
      throw err;
    }
  };

  // --- Table Logic ---
  const updateTableState = (rowIndex, columnId, value) => {
    setData(old => old.map((record, index) => (
      index === rowIndex
        ? { ...record, [columnId]: value, modified: true }
        : record
    )));
  };

  const buildSaveErrorMessage = () => "Network Error: Could not save record.";

  const editableColumnIds = [
    "cityscope", "cityscope_name", "appendix", "issue_date",
    "appendix_header", "non_unit_scope", "include_globe", "allow_editing",
    "led_occs", "output_type",
  ];

  const handleSave = async ({ row, column, value }) => {
    const recordToSave = {
      ...row.original,
      [column.id]: value,
      last_edited_by: user,
      modified: true,
    };

    const cityscopeValue = String(recordToSave.cityscope ?? "").trim();
    if (!cityscopeValue) {
      setUi({ showAlert: true, errorMessage: "Cityscope cannot be blank. Record not saved." });
      return;
    }

    if (recordToSave.isSaving) return;

    setUi({ showAlert: false, errorMessage: "" });

    setData(old => old.map((r, index) =>
      index === row.index ? { ...r, isSaving: true, [column.id]: value } : r
    ));

    try {
      const response = await saveRecord(recordToSave);

      if (response?.result === false) {
        setData(old => old.map((r, index) => index === row.index ? { ...r, isSaving: false } : r));
        setUi({ showAlert: true, errorMessage: buildSaveErrorMessage(response.error_message) });
        return;
      }

      setData(old => old.map((r, index) =>
        index === row.index
          ? { ...r, [column.id]: value, modified: false, isSaving: false, new_record: false }
          : r
      ));
      setUi({ showAlert: false, errorMessage: "" });
    } catch (err) {
      setData(old => old.map((r, index) => index === row.index ? { ...r, isSaving: false } : r));
      let errBody = null;
      try {
        errBody = await err.response.body.json();
      } catch (_) {}
      setUi({ showAlert: true, errorMessage: buildSaveErrorMessage(errBody) });
    }
  };

  const addRecord = () => {
    setData(prev => [{
      appendix: 0,
      include_globe: false,
      allow_editing: false,
      led_occs: false,
      created_by: user,
      last_edited_by: user,
      new_record: true,
    }, ...prev]);
  };

  const createId = useMemo(
    () => createElementIdFactory(cityscopeIdPrefix),
    [cityscopeIdPrefix]
  );

  // --- Column Definitions ---
  const columns = useMemo(() => [
    columnHelper.accessor("schedule", {
      id: "schedule",
      header: "",
      cell: ({ row }) => (
        <Link
          id={createId("schedule", "link", row.id)}
          to={{
            pathname: `/schedules/${row.original.cityscope}`,
          }}
        >
          <Button id={createId("schedule", "button", row.id)} type="button" size="sm" variant="primary">
            View Schedule
          </Button>
        </Link>
      ),
      size: 120,
      enableSorting: false,
    }),
    columnHelper.accessor("cityscope", {
      id: "cityscope",
      header: "Cityscope",
      cell: EditableCell,
      size: 150,
    }),
    columnHelper.accessor("cityscope_name", {
      id: "cityscope_name",
      header: "Name",
      cell: EditableCell,
      size: 250,
    }),
    columnHelper.accessor("appendix", {
      id: "appendix",
      header: "Appendix",
      cell: EditableCell,
      size: 150,
    }),
    columnHelper.accessor("issue_date", {
      id: "issue_date",
      header: "Issue Date",
      cell: EditableCell,
      size: 150,
    }
    ),
    columnHelper.accessor("appendix_header", {
      id: "appendix_header",
      header: "Appendix Header",
      cell: EditableCell,
      size: 150,
    }),
    columnHelper.accessor("non_unit_scope", {
      id: "non_unit_scope",
      header: "Non Unit scope",
      cell: EditableCell,
      size: 150,
    }),
    columnHelper.accessor("include_globe", {
      id: "include_globe",
      header: "Include Globe",
      cell: EditableCell,
      size: 150,
    }),
    columnHelper.accessor("allow_editing", {
      id: "allow_editing",
      header: "Allow Editing",
      cell: EditableCell,
      size: 150,
    }),
    columnHelper.accessor("led_occs", {
      id: "led_occs",
      header: "LED Occs",
      cell: EditableCell,
      size: 150,
    }),
    columnHelper.accessor("output_type", {
      id: "output_type",
      header: "Output Type",
      cell: EditableCell,
      size: 150,
    }),
  ], [createId]);

  const tableElementIds = useMemo(
    () => createReusableTableElementIds(createId),
    [createId]
  );

  if (!isAuthenticated) return null;

  // --- Render Functions ---
  return (
    <div className="mt-3">
      <Alert show={ui.showAlert} variant="danger" onClose={() => setUi({ ...ui, showAlert: false })} dismissible>
        {ui.errorMessage}
      </Alert>

      <Col className="text-right" style={{ marginBottom: '12px' }}>
        <Button id={createId("add", "button")} variant="secondary" size="sm" onClick={addRecord}>Add</Button>
      </Col>

      <Row>
        <Col>
          {isLoading ? (
            <div className="outer-div">
              <div className="sweet-loading inner-div">
                <PacmanLoader
                  cssOverride={{ display: "block", margin: "0 auto" }}
                  size={20}
                  color={"#e03c31"}
                  loading={isLoading}
                />
              </div>
            </div>
          ) : (
            <div className="custom-data-table">
              <GenericTable
                columns={columns}
                data={data}
                section="cityscope-list"
                elementIds={tableElementIds}
                pageSize={PAGE_SIZES.cityscope}
                enablePagination={true}
                state={{ sorting }}
                onSortingChange={setSorting}
                enableSortingRemoval={false}
                meta={{
                  updateData: updateTableState,
                  handleSave: handleSave,
                  shouldForceSaveOnBlur: ({ row, column }) =>
                    editableColumnIds.includes(column?.id) && !!row?.original?.cityscope,
                }}
              />
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
}

export default CityscopeList;